import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  CheckCircle,
  Code,
  Cloud,
  Security,
  Speed,
  Api,
  DataObject,
  Sync,
  ExpandMore,
  ArrowForward,
  Architecture,
  BugReport,
  Timeline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const CustomSoftwarePage: React.FC = () => {
  const navigate = useNavigate();

  const capabilities = [
    {
      title: 'Legacy System Modernization',
      icon: <Architecture />,
      description: 'Transform outdated government systems into modern, cloud-native applications while maintaining data integrity and compliance.',
      technologies: ['Microservices', 'Containerization', 'API-First Design', 'Cloud Migration'],
    },
    {
      title: 'Custom Application Development',
      icon: <Code />,
      description: 'Build secure, scalable applications tailored to specific agency requirements and mission-critical operations.',
      technologies: ['React/Angular', 'Node.js/.NET', 'PostgreSQL', 'AWS/Azure'],
    },
    {
      title: 'API Development & Integration',
      icon: <Api />,
      description: 'Create robust APIs and integrate disparate systems for seamless data flow across government departments.',
      technologies: ['RESTful APIs', 'GraphQL', 'OAuth 2.0', 'API Gateway'],
    },
    {
      title: 'DevSecOps Implementation',
      icon: <Security />,
      description: 'Implement security-first CI/CD pipelines with automated testing and compliance checks.',
      technologies: ['Jenkins/GitLab CI', 'Docker/Kubernetes', 'SonarQube', 'OWASP Tools'],
    },
  ];

  const process = [
    {
      phase: 'Discovery & Analysis',
      duration: '1-2 weeks',
      activities: [
        'Requirements gathering',
        'Stakeholder interviews',
        'Technical assessment',
        'Compliance review',
      ],
    },
    {
      phase: 'Design & Architecture',
      duration: '2-3 weeks',
      activities: [
        'Solution architecture',
        'UI/UX design',
        'Security planning',
        'Technical specifications',
      ],
    },
    {
      phase: 'Development & Testing',
      duration: '8-16 weeks',
      activities: [
        'Agile sprints',
        'Code development',
        'Automated testing',
        'Security scanning',
      ],
    },
    {
      phase: 'Deployment & Support',
      duration: 'Ongoing',
      activities: [
        'Production deployment',
        'Performance monitoring',
        'Maintenance & updates',
        '24/7 support',
      ],
    },
  ];

  const technologies = {
    'Frontend': ['React', 'Angular', 'Vue.js', 'TypeScript', 'Material-UI', 'Bootstrap'],
    'Backend': ['Node.js', '.NET Core', 'Python/Django', 'Java Spring', 'Go', 'Ruby on Rails'],
    'Database': ['PostgreSQL', 'MongoDB', 'MySQL', 'Oracle', 'Redis', 'Elasticsearch'],
    'Cloud': ['AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes', 'Terraform'],
    'Security': ['OAuth 2.0', 'JWT', 'SSL/TLS', 'WAF', 'Encryption', 'MFA'],
  };

  const faqs = [
    {
      question: 'How do you ensure compliance with government regulations?',
      answer: 'We follow FedRAMP, FISMA, and NIST guidelines throughout development. Our team includes certified security professionals who conduct regular audits and implement required controls from day one.',
    },
    {
      question: 'What is your approach to legacy system modernization?',
      answer: 'We use a phased approach that minimizes disruption. We start with API wrappers around legacy systems, gradually migrating functionality to modern architectures while maintaining parallel operations until fully tested.',
    },
    {
      question: 'Do you provide ongoing maintenance and support?',
      answer: 'Yes, we offer comprehensive maintenance packages including 24/7 monitoring, regular updates, security patches, and feature enhancements. Our SLAs guarantee response times based on issue severity.',
    },
    {
      question: 'How long does a typical custom software project take?',
      answer: 'Project timelines vary based on complexity. Simple applications may take 3-4 months, while enterprise systems can take 6-12 months. We use agile methodology to deliver working software in iterations.',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3a8f 0%, #3b82f6 100%)',
          color: 'white',
          pt: 12,
          pb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h1" gutterBottom sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
                Custom Software Development
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.95 }}>
                Enterprise-grade applications built for government and commercial sectors 
                with security, compliance, and scalability at the core.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button 
                  variant="contained" 
                  size="large"
                  color="secondary"
                  onClick={() => navigate('/contact')}
                  endIcon={<ArrowForward />}
                >
                  Start Your Project
                </Button>
                <Button 
                  variant="outlined" 
                  size="large"
                  sx={{ 
                    color: 'white', 
                    borderColor: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                    }
                  }}
                >
                  View Case Studies
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Code sx={{ fontSize: 200, opacity: 0.3 }} />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Key Benefits */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" gutterBottom textAlign="center">
            Why Choose Our Development Services
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Security sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6">Security First</Typography>
                <Typography variant="body2" color="text.secondary">
                  Built-in security from the ground up with continuous vulnerability scanning
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Speed sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6">Agile Delivery</Typography>
                <Typography variant="body2" color="text.secondary">
                  Iterative development with regular demos and continuous deployment
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Cloud sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6">Cloud Native</Typography>
                <Typography variant="body2" color="text.secondary">
                  Scalable architectures designed for modern cloud platforms
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <DataObject sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6">API-First</Typography>
                <Typography variant="body2" color="text.secondary">
                  Modular design enabling easy integration and future expansion
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Capabilities */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h2" gutterBottom textAlign="center">
            Our Development Capabilities
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {capabilities.map((capability) => (
              <Grid item xs={12} md={6} key={capability.title}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ color: 'primary.main', mr: 2 }}>
                        {capability.icon}
                      </Box>
                      <Typography variant="h5" fontWeight={600}>
                        {capability.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" paragraph>
                      {capability.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {capability.technologies.map((tech) => (
                        <Chip 
                          key={tech}
                          label={tech}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Development Process */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" gutterBottom textAlign="center">
            Our Development Process
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {process.map((phase, index) => (
              <Grid item xs={12} md={3} key={phase.phase}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Timeline sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="h6" fontWeight={600}>
                      Phase {index + 1}
                    </Typography>
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {phase.phase}
                  </Typography>
                  <Chip 
                    label={phase.duration}
                    size="small"
                    color="primary"
                    sx={{ mb: 2 }}
                  />
                  <List dense>
                    {phase.activities.map((activity) => (
                      <ListItem key={activity} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                        </ListItemIcon>
                        <ListItemText primary={activity} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Technology Stack */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h2" gutterBottom textAlign="center">
            Technology Stack
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {Object.entries(technologies).map(([category, techs]) => (
              <Grid item xs={12} md={4} lg={2.4} key={category}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    {category}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {techs.map((tech) => (
                      <Chip 
                        key={tech}
                        label={tech}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQs */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="md">
          <Typography variant="h2" gutterBottom textAlign="center">
            Frequently Asked Questions
          </Typography>
          <Box sx={{ mt: 4 }}>
            {faqs.map((faq, index) => (
              <Accordion key={index}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">{faq.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>{faq.answer}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" gutterBottom>
            Ready to Build Your Custom Solution?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4 }}>
            Let's discuss your project requirements and create a development roadmap.
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            sx={{ 
              bgcolor: 'white',
              color: 'primary.main',
              px: 4,
              '&:hover': {
                bgcolor: 'grey.100',
              }
            }}
            onClick={() => navigate('/contact')}
          >
            Schedule Technical Consultation
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default CustomSoftwarePage;